import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PenLine, Wand2 } from 'lucide-react';
import ContentDeclineBanner from '../ContentDeclineBanner';
import ThemeTextarea from '../create/ThemeTextarea';
import ThemeHistoryControls from '../create/ThemeHistoryControls';
import { LIFE_EVENT_EMOJI } from '../../lib/lifeEvents';
import type { LifeSelection } from '../../lib/lifeEvents';
import { isInterestKey } from '../../lib/interests';
import { PRESETS } from './presets';
import { nextWandIdea, resolveThemeTag, rowKeys as computeRowKeys } from './ideaCore';
import useIdeaCards from './useIdeaCards';
import ThemeChipRow from './ThemeChipRow';
import IdeaCards, { CARD_COUNT } from './IdeaCards';
import IncludeNamePill from './IncludeNamePill';
import ModeDoors from './ModeDoors';
import type { IdeaComposerProps, IdeaLength, IdeaMode, Selection } from './types';

/**
 * The one story-idea dialog (2026-09-04), configured per use case
 * (components/idea/presets.ts): theme chips + "More" → idea cards (medium by
 * default, length ladder, shuffle) → an always-visible idea box with the mic
 * and a browsable back/forward history. The account wizard, the /try page,
 * the reader's one-tap slide and the native first run (/welcome, 2026-10-02)
 * all render this; what differs is the preset and the props the page hands in
 * (recent themes, life event, name pill, content screen are account-only).
 *
 * Rules carried over from the picker step (2026-08-28/29):
 *  - nothing is pre-selected on a fresh account; the first cards are
 *    "ideas for tonight"; a handed-over theme beats the recent-theme default;
 *  - a picked card belongs to the list it came from — other theme, other
 *    length or shuffle drop it; typed text is the parent's and stays;
 *  - typed text folds the cards away ("show ideas" brings them back);
 *  - stories.theme_tag is set only while the text equals a fetched idea of a
 *    chip (ideaCore.resolveThemeTag).
 */
export default function IdeaComposer({
  surface, language, themeHistory, voice, tone = 'light',
  initialSelection = null, initialThemeTag = null, pinKeys, loadRecentThemes, ideasContext = '',
  onThemeTag, ideaLength: controlledLength, onIdeaLength, lifeEvent, includeName, screen, nameFromText = false, knownTexts,
  autoApplyFirstIdea = false, onDraftChange, textareaPlaceholder, textareaLabel, textareaMaxLength,
  initialMode, onModeChange, onOwnIdea, textareaEditHint, textareaAutoGrowMax,
  heroName: heroNameProp = '', childHero = false, leadCard = null, onSelection,
}: IdeaComposerProps) {
  const { t } = useTranslation();
  const preset = PRESETS[surface];
  const [recent, setRecent] = useState<string[]>([]);
  const [selection, setSelection] = useState<Selection>(
    initialThemeTag && isInterestKey(initialThemeTag) ? { kind: 'key', key: initialThemeTag } : null,
  );
  const [internalLength, setInternalLength] = useState<IdeaLength>('medium');
  const ideaLength = controlledLength ?? internalLength;
  const [cardsPinnedOpen, setCardsPinnedOpen] = useState(false);
  // Doors (trial): guided by default; a box that already has text (a draft, a seeded idea)
  // opens on "Eigene Idee". One history behind both doors — switching never drops text.
  const [mode, setMode] = useState<IdeaMode>(() => initialMode ?? (themeHistory.isDirty ? 'own' : 'guided'));
  const changeMode = (next: IdeaMode) => {
    if (next === mode) return;
    setMode(next);
    onModeChange?.(next);
  };
  const guidedVisible = !preset.doors || mode === 'guided';
  // Box on demand (first run, 2026-10-02): opened by the "Eigene Idee" pill; typed text keeps it open (below, once the
  // ideas are known: so does any idea that is no visible card — a softened text, a pick from an earlier visit).
  const [ownOpen, setOwnOpen] = useState(false);
  // Slide: ideas the wand (or the auto-apply) already offered.
  const offeredRef = useRef<string[]>([]);
  const valueRef = useRef(themeHistory.value);
  valueRef.current = themeHistory.value;

  // The ideas star the child while the name pill is on. On the slide (nameFromText:
  // no name field) the parent's EDITED text goes to the ideas endpoint as `theme`,
  // and the model decides whether it names a child — German capitalises every
  // noun, so no heuristic on our side (2026-09-08). Text we put there ourselves
  // (the prefilled premise, offered ideas) is not a hint. The first run (2026-10-02) names the hero without a pill:
  // the first name from its "Für wen" step, and every idea stars a child (`childHero`).
  const heroName = includeName?.on ? includeName.name : heroNameProp;
  const ours = [...(knownTexts ?? []), ...offeredRef.current];
  const themeHint = nameFromText && themeHistory.value.trim() && !ours.includes(themeHistory.value) ? themeHistory.value : '';
  const { batch, loading, load } = useIdeaCards(language, heroName, themeHint, nameFromText || childHero, surface);
  const shownCards = [...(leadCard ? [leadCard.text] : []), ...(batch ? batch.ideas.slice(0, CARD_COUNT - (leadCard ? 1 : 0)) : [])];
  const offCard = !loading && !!batch && !!themeHistory.value.trim() && !shownCards.includes(themeHistory.value);
  const boxVisible = preset.boxOnDemand
    ? ownOpen || themeHistory.isDirty || offCard
    : !preset.doors || mode === 'own' || themeHistory.value.trim().length > 0;
  const batchHintRef = useRef('');

  const lifeLabel = (key: string) => t(`lifeEvents.items.${key}`);
  const lifeValue: LifeSelection = lifeEvent?.value ?? null;
  const contextFor = (life: LifeSelection) => (life ? (life.kind === 'key' ? lifeLabel(life.key) : life.text) : ideasContext);
  const contextText = contextFor(lifeValue);

  const { rowKeys, moreKeys } = useMemo(() => computeRowKeys([...(pinKeys ?? []), ...recent]), [pinKeys, recent]);

  // Mount: recent themes (account), then the first ideas — for the restored /
  // handed-over / last theme when there is one, otherwise tonight's ideas.
  useEffect(() => {
    let cancelled = false;
    const recentPromise = loadRecentThemes ? loadRecentThemes().catch(() => [] as string[]) : Promise.resolve([] as string[]);
    recentPromise.then(async (keys) => {
      if (cancelled) return;
      setRecent(keys);
      const initial: Selection = selection ?? initialSelection ?? (keys[0] ? { kind: 'key', key: keys[0] } : null);
      setSelection(initial);
      const first = await load(initial, ideaLength, contextText);
      // One-tap flows: the first idea goes straight into the (still empty) box.
      if (cancelled || !first || first.fallback || !autoApplyFirstIdea) return;
      if (!valueRef.current.trim()) { offeredRef.current = [first.ideas[0]]; themeHistory.applyPill(first.ideas[0]); }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    onThemeTag?.(resolveThemeTag(batch, themeHistory.value));
  }, [batch, themeHistory.value, onThemeTag]);

  useEffect(() => {
    onDraftChange?.(themeHistory.value);
  }, [themeHistory.value, onDraftChange]);

  useEffect(() => {
    onSelection?.(selection);
  }, [selection, onSelection]);

  // A picked card belongs to the list it came from; when that list is replaced
  // (other theme, other length, shuffle) drop it. Typed text is the parent's and stays.
  const dropPickedCard = () => {
    if (!themeHistory.isDirty && themeHistory.value) themeHistory.reset('');
  };
  const select = (next: Selection) => {
    setSelection(next);
    dropPickedCard();
    load(next, ideaLength, contextText);
  };
  const changeLife = (next: LifeSelection) => {
    lifeEvent?.onChange(next);
    dropPickedCard();
    // contextText is from this render's closure; pass the fresh value explicitly.
    load(selection, ideaLength, contextFor(next));
  };
  const selectLength = (length: IdeaLength) => {
    if (length === ideaLength) return;
    onIdeaLength?.(length);
    if (controlledLength === undefined) setInternalLength(length);
    dropPickedCard();
    load(selection, length, contextText);
  };
  const shuffle = () => {
    dropPickedCard();
    load(selection, ideaLength, contextText, true);
  };
  const pickCard = (text: string) => {
    if (themeHistory.value === text) { themeHistory.reset(''); return; }
    themeHistory.applyPill(text);
    screen?.run(text);
  };
  // Slide wand: the next fetched idea not offered yet (the endpoint returns four),
  // a fresh set once they are used up. Inserted after the cursor like a card,
  // so the history controls browse everything seen.
  const wand = async () => {
    const used = [...offeredRef.current, themeHistory.value];
    // The parent edited the text since this batch was fetched: the next ideas must
    // be made with that text in view (a named child stays), so fetch fresh ones.
    const stale = nameFromText && batchHintRef.current !== themeHint;
    let next = batch && !stale ? nextWandIdea(batch.ideas, used) : null;
    if (!next) {
      batchHintRef.current = themeHint;
      const fresh = await load(selection, ideaLength, contextText, true);
      if (!fresh) return;
      next = nextWandIdea(fresh.ideas, used) ?? fresh.ideas.find((i) => i !== themeHistory.value) ?? null;
    }
    if (!next) return;
    offeredRef.current = [...offeredRef.current, next];
    themeHistory.applyPill(next);
  };

  // Another language (the /try selector): the cards are refetched (the cache is
  // keyed by language); an un-typed pick from the old language is dropped.
  const prevLanguage = useRef(language);
  useEffect(() => {
    if (prevLanguage.current === language) return;
    prevLanguage.current = language;
    dropPickedCard();
    load(selection, ideaLength, contextText);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const lifeProp = lifeEvent ? { value: lifeValue, onChange: changeLife } : undefined;
  const cardsCollapsed = themeHistory.isDirty && !cardsPinnedOpen;
  const titleSuffix = lifeValue ? (
    <span className="text-muted-foreground font-normal" data-testid={`${preset.prefix}-life-title`}>
      {' · '}{lifeValue.kind === 'key' ? `${LIFE_EVENT_EMOJI[lifeValue.key]} ${lifeLabel(lifeValue.key)}` : lifeValue.text}
    </span>
  ) : null;
  const labelTone = tone === 'dark' ? 'text-white' : 'text-foreground';
  // "Eigene Idee": empties the box and focuses it (the mic is right there); the history's
  // back arrow brings the idea back. Sits next to the wand as its sibling pill (Kai, 2026-09-13:
  // "Andere Idee" / "Eigene Idee" side by side is the actual choice; a button under the CTA read
  // as the second call to action).
  const ownIdea = () => {
    setOwnOpen(true);
    themeHistory.setUserValue('');
    onOwnIdea?.();
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLTextAreaElement>(`[data-testid="${preset.textareaTestId}"]`);
      el?.focus();
    });
  };
  const pill = tone === 'dark'
    ? 'bg-white/10 text-white/85 hover:bg-primary/30 hover:text-white disabled:opacity-50'
    : 'bg-stone-100 text-foreground hover:bg-primary/10 hover:text-primary disabled:opacity-50';

  return (
    <div className="space-y-4 md:space-y-6" data-testid="idea-composer" data-surface={surface}>
      {screen?.latest && (
        <ContentDeclineBanner category={screen.latest.category} softening={screen.softening} onSoften={screen.onSoften} />
      )}

      {preset.doors && <ModeDoors prefix={preset.prefix} mode={mode} onChange={changeMode} />}

      {preset.chips && guidedVisible && (
        <ThemeChipRow preset={preset} rowKeys={rowKeys} moreKeys={moreKeys} recent={recent} selection={selection} onSelect={select} lifeEvent={lifeProp} tone={tone} />
      )}

      {includeName && (
        <IncludeNamePill name={includeName.name} on={includeName.on} onToggle={includeName.onToggle} />
      )}

      {preset.cards && guidedVisible && (
        <IdeaCards
          prefix={preset.prefix}
          batch={batch}
          loading={loading}
          length={ideaLength}
          onLength={selectLength}
          onShuffle={shuffle}
          value={themeHistory.value}
          onPick={pickCard}
          collapsed={preset.doors ? false : cardsCollapsed}
          onShowIdeas={() => setCardsPinnedOpen(true)}
          titleSuffix={titleSuffix}
          compact={preset.compactCards}
          tone={tone}
          lead={leadCard}
          onOwnIdea={preset.boxOnDemand ? ownIdea : undefined}
        />
      )}

      {/* The idea box: prefilled on the one-tap surfaces (no extra tap to edit — Kai, 2026-09-04),
          with the wand + history above it; the wizard/trial show the label + history instead. */}
      {boxVisible && (
      <div className="space-y-1.5 text-left">
        <div className="flex flex-wrap items-center justify-between gap-2">
          {preset.wand ? (
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={wand}
                disabled={loading}
                data-testid={`${preset.prefix}-wand`}
                className={`inline-flex items-center gap-1.5 min-h-10 px-3.5 rounded-full text-sm transition-colors ${pill}`}
              >
                <Wand2 className={`w-4 h-4 ${loading ? 'animate-pulse' : ''}`} />
                {t('idea.wand')}
              </button>
              {preset.ownIdeaLink && (
                <button
                  type="button"
                  onClick={ownIdea}
                  data-testid={`${preset.prefix}-own-idea`}
                  className={`inline-flex items-center gap-1.5 min-h-10 px-3.5 rounded-full text-sm transition-colors ${pill}`}
                >
                  <PenLine className="w-4 h-4" aria-hidden="true" />
                  {t('idea.own')}
                </button>
              )}
            </div>
          ) : (
            <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${labelTone}`}>
              <PenLine className="w-4 h-4 text-primary" /> {preset.doors && mode === 'guided' ? t('idea.yourIdea') : (textareaLabel ?? t('create.picker.ownIdea'))}
            </span>
          )}
          <ThemeHistoryControls themeHistory={themeHistory} tone={tone} />
        </div>
        <ThemeTextarea
          themeHistory={themeHistory}
          voice={voice}
          onBlur={screen?.onThemeBlur}
          compact
          autoGrow={preset.autoGrow}
          autoGrowMax={textareaAutoGrowMax}
          tone={tone}
          placeholder={preset.wand && loading && !themeHistory.value ? '' : textareaPlaceholder}
          testId={preset.textareaTestId}
          maxLength={textareaMaxLength}
          editHint={textareaEditHint}
        />
        {preset.doors && mode === 'own' && (
          /* Three tappable examples under the empty box: what "an idea" looks like here. */
          <div className="flex flex-wrap gap-2 pt-1" data-testid={`${preset.prefix}-examples`}>
            {(['idea.example1', 'idea.example2', 'idea.example3'] as const).map((key, i) => (
              <button
                key={key}
                type="button"
                onClick={() => pickCard(t(key))}
                data-testid={`${preset.prefix}-example-${i}`}
                className="min-h-9 px-3 rounded-full bg-stone-100 text-xs md:text-sm text-foreground hover:bg-primary/10 hover:text-primary transition-colors"
              >
                {t(key)}
              </button>
            ))}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
